import { NodeType } from '@/enums/node-type.enum';

// Mirrors core's tool-required-inputs.utils.ts, so a run the client lets through is not refused for
// a missing required value.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasText = (value: unknown): boolean => typeof value === 'string' && value.trim() !== '';

const isUsableListItem = (item: unknown): boolean => (typeof item === 'string' ? item.trim() !== '' : item != null);

/** A file a node can fetch: a delivery URL, or a Blobstore key the runner resolves. */
const isUsableFile = (file: unknown): boolean =>
  isRecord(file) && (hasText(file.url) || (isRecord(file.blobstore) && hasText(file.blobstore.id)));

/**
 * Whether `value` is something an input of this type can run with. An empty prompt or string, a
 * missing file and an empty list are no value; a number, boolean, seed or chosen option is one.
 */
export function hasUsableInputValue(input: { type?: string; isIterator?: boolean }, value: unknown): boolean {
  if (value == null) return false;
  switch (input.type) {
    case NodeType.Prompt:
    case NodeType.PromptV3:
      return isRecord(value) && hasText(value.prompt);
    case NodeType.String:
      return isRecord(value) && hasText(value.string);
    case NodeType.Import:
      return isRecord(value) && (isUsableFile(value.file) || hasText(value.url));
    case NodeType.MediaIterator:
      return isRecord(value) && Array.isArray(value.files) && value.files.some(isUsableFile);
    case NodeType.ColorPalette:
      return isRecord(value) && Array.isArray(value.colors) && value.colors.length > 0;
    case NodeType.Array:
      return Array.isArray(value) && value.some(isUsableListItem);
    case NodeType.MultiLora:
      // Core reads a single-mode LoRA as a bare string and a selector as `selectedLora`.
      return hasText(value) || (isRecord(value) && isRecord(value.selectedLora) && hasText(value.selectedLora.file));
    case NodeType.MuxV2:
      if (!input.isIterator) return true;
      return isRecord(value) && Array.isArray(value.options) && value.options.some(isUsableListItem);
    default:
      return true;
  }
}
